import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { 
  Save, RefreshCw, Link as LinkIcon, Database, Upload, Download, 
  Mail, Brain, FileText, Server, Check, X, TestTube, Settings as SettingsIcon,
  ChevronDown, ChevronUp, Edit2, Plus, Trash2, Clock
} from 'lucide-react';

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [microsoftStatus, setMicrosoftStatus] = useState(null);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [sourceConfigs, setSourceConfigs] = useState([]);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceForm, setSourceForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Section collapse state - all collapsed by default
  const [collapsedSections, setCollapsedSections] = useState({
    scheduler: true,
    plugins: true,
    templates: true,
    sourceSystem: true,
    branding: true,
    account: true,
    emailAccess: true
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Plugin state
  const [pluginConfigs, setPluginConfigs] = useState([]);
  const [availableProviders, setAvailableProviders] = useState({});
  const [editingPlugin, setEditingPlugin] = useState(null);
  const [pluginForm, setPluginForm] = useState({});
  const [testingPlugin, setTestingPlugin] = useState(null);
  const [expandedType, setExpandedType] = useState(null);

  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({});
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchMicrosoftStatus();
    fetchGmailStatus();
    fetchSourceConfigs();
    if (isAdmin) {
      fetchPluginConfigs();
      fetchAvailableProviders();
      fetchEmailTemplates();
    }
  }, [isAdmin]);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/config');
      setConfig(response.data.config);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMicrosoftStatus = async () => {
    try {
      const response = await api.get('/auth/microsoft/status');
      setMicrosoftStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Microsoft status:', err);
    }
  };

  const fetchGmailStatus = async () => {
    try {
      const response = await api.get('/auth/google/status');
      setGmailStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch Gmail status:', err);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await api.get('/auth/google/connect');
      window.location.href = response.data.authUrl;
    } catch (err) {
      alert('Failed to initiate Gmail connection');
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail account?')) return;
    try {
      await api.post('/auth/google/disconnect');
      setGmailStatus({ connected: false });
    } catch (err) {
      alert('Failed to disconnect Gmail');
    }
  };

  const fetchSourceConfigs = async () => {
    try {
      const response = await api.get('/source-system');
      setSourceConfigs(response.data.configs || []);
    } catch (err) {
      console.error('Failed to fetch source configs:', err);
    }
  };

  const fetchPluginConfigs = async () => {
    try {
      const response = await api.get('/plugins');
      setPluginConfigs(response.data.configs || []);
    } catch (err) {
      console.error('Failed to fetch plugin configs:', err);
    }
  };

  const fetchAvailableProviders = async () => {
    try {
      const response = await api.get('/plugins/available');
      setAvailableProviders(response.data.providers || {});
    } catch (err) {
      console.error('Failed to fetch available providers:', err);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/config/templates');
      setEmailTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template.templateId);
    setTemplateForm({
      templateName: template.templateName,
      subjectTemplate: template.subjectTemplate,
      bodyTemplate: template.bodyTemplate,
      isActive: template.isActive
    });
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.put(`/config/templates/${editingTemplate}`, templateForm);
      alert('Template saved successfully');
      setEditingTemplate(null);
      fetchEmailTemplates();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCancelEditTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({});
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/config', config);
      alert('Settings saved successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectMicrosoft = async () => {
    try {
      const response = await api.get('/auth/microsoft/connect');
      window.location.href = response.data.authUrl;
    } catch (err) {
      alert('Failed to initiate Microsoft connection');
    }
  };

  const handleDisconnectMicrosoft = async () => {
    if (!window.confirm('Disconnect Microsoft account?')) return;
    try {
      await api.delete('/auth/microsoft/disconnect');
      setMicrosoftStatus({ connected: false });
    } catch (err) {
      alert('Failed to disconnect');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      await api.put('/auth/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      alert('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to change password');
    }
  };

  const handleEditSource = (config) => {
    setEditingSource(config.purpose);
    setSourceForm({
      systemName: config.systemName,
      connectionType: config.connectionType,
      apiEndpoint: config.apiEndpoint || '',
      apiKey: '',
      apiAuthType: config.apiAuthType || 'BEARER',
      fileFormat: config.fileFormat || 'EXCEL',
      isEnabled: config.isEnabled
    });
  };

  const handleSaveSource = async () => {
    try {
      await api.put(`/source-system/${editingSource}`, sourceForm);
      alert('Source system configuration saved');
      setEditingSource(null);
      fetchSourceConfigs();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  // Plugin handlers
  const handleEditPlugin = (plugin) => {
    setEditingPlugin(plugin.configId);
    setPluginForm({
      configName: plugin.configName,
      isEnabled: plugin.isEnabled,
      priority: plugin.priority,
      ...plugin.config
    });
  };

  const handleSavePlugin = async () => {
    try {
      const { configName, isEnabled, priority, ...configFields } = pluginForm;
      await api.put(`/plugins/${editingPlugin}`, {
        configName,
        isEnabled,
        priority,
        config: configFields
      });
      alert('Plugin configuration saved');
      setEditingPlugin(null);
      fetchPluginConfigs();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const handleTogglePlugin = async (plugin) => {
    try {
      await api.put(`/plugins/${plugin.configId}/enable`, {
        enabled: !plugin.isEnabled
      });
      fetchPluginConfigs();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle plugin');
    }
  };

  const handleTestPlugin = async (plugin) => {
    setTestingPlugin(plugin.configId);
    try {
      const response = await api.post(`/plugins/${plugin.configId}/test`);
      if (response.data.success) {
        alert('✅ Connection successful!');
      } else {
        alert(`❌ Connection failed: ${response.data.error}`);
      }
    } catch (err) {
      alert(`❌ Test failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setTestingPlugin(null);
    }
  };

  const getPluginIcon = (type) => {
    switch (type) {
      case 'EMAIL': return <Mail size={16} />;
      case 'AI': return <Brain size={16} />;
      case 'TEMPLATE': return <FileText size={16} />;
      case 'DATA': return <Database size={16} />;
      default: return <Server size={16} />;
    }
  };

  const getPluginTypeLabel = (type) => {
    switch (type) {
      case 'EMAIL': return 'Email Providers';
      case 'AI': return 'AI Providers';
      case 'TEMPLATE': return 'Template Engines';
      case 'DATA': return 'Data Providers';
      default: return type;
    }
  };

  const getProviderLabel = (type, code) => {
    const providers = availableProviders[type] || [];
    const provider = providers.find(p => p.code === code);
    return provider?.name || code;
  };

  const renderPluginConfigFields = (plugin) => {
    const providers = availableProviders[plugin.pluginType] || [];
    const provider = providers.find(p => p.code === plugin.providerCode);
    const schema = provider?.configSchema?.properties || {};

    return Object.entries(schema).map(([key, field]) => (
      <div className="form-group" key={key}>
        <label className="form-label">{field.description || key}</label>
        <input
          type={field.encrypted ? 'password' : 'text'}
          className="form-input"
          value={pluginForm[key] || ''}
          placeholder={plugin.config?.[`has${key.charAt(0).toUpperCase() + key.slice(1)}`] ? '••••••••' : ''}
          onChange={(e) => setPluginForm(prev => ({ ...prev, [key]: e.target.value }))}
        />
      </div>
    ));
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  const pluginsByType = {};
  pluginConfigs.forEach(p => {
    if (!pluginsByType[p.pluginType]) pluginsByType[p.pluginType] = [];
    pluginsByType[p.pluginType].push(p);
  });

  return (
    <div style={{ maxWidth: '800px' }}>
      <h1 className="mb-6" style={{ fontSize: '1.5rem', fontWeight: '600' }}>Settings</h1>

      {/* Profile Settings */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">Profile</h2>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div className="text-sm text-muted">Name</div>
              <div className="font-medium">{user?.fullName}</div>
            </div>
            <div>
              <div className="text-sm text-muted">Email</div>
              <div className="font-medium">{user?.email}</div>
            </div>
            <div>
              <div className="text-sm text-muted">Role</div>
              <div className="font-medium">{user?.role?.roleName}</div>
            </div>
            <div>
              <div className="text-sm text-muted">Client</div>
              <div className="font-medium">{user?.client?.clientName}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="card mb-6">
        <div 
          className="card-header" 
          style={{ cursor: 'pointer' }}
          onClick={() => toggleSection('account')}
        >
          <h2 className="card-title">Change Password</h2>
          {collapsedSections.account ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </div>
        {!collapsedSections.account && (
        <>
        <form onSubmit={handleChangePassword}>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                required
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="card-footer">
            <button type="submit" className="btn btn-primary">
              Change Password
            </button>
          </div>
        </form>
        </>
        )}
      </div>

      {/* Email Access - Combined Microsoft and Gmail */}
      <div className="card mb-6">
        <div 
          className="card-header" 
          style={{ cursor: 'pointer' }}
          onClick={() => toggleSection('emailAccess')}
        >
          <h2 className="card-title flex items-center gap-2">
            <Mail size={18} />
            Email Access
          </h2>
          {collapsedSections.emailAccess ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </div>
        {!collapsedSections.emailAccess && (
        <div className="card-body">
          <p className="text-sm text-muted mb-4">
            Connect your email account to send and receive emails directly from the application.
          </p>

          {/* Microsoft 365 */}
          <div 
            style={{ 
              padding: '16px', 
              background: 'var(--bg-secondary)', 
              borderRadius: '8px',
              marginBottom: '12px'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  background: '#0078d4', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Mail size={20} color="white" />
                </div>
                <div>
                  <div className="font-medium">Microsoft 365 / Outlook</div>
                  {microsoftStatus?.connected ? (
                    <div className="text-sm text-success flex items-center gap-1">
                      <Check size={14} />
                      Connected as {microsoftStatus.email}
                    </div>
                  ) : (
                    <div className="text-sm text-muted">Not connected</div>
                  )}
                </div>
              </div>
              {microsoftStatus?.connected ? (
                <button className="btn btn-outline btn-sm" onClick={handleDisconnectMicrosoft}>
                  Disconnect
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={handleConnectMicrosoft}>
                  <LinkIcon size={14} />
                  Connect
                </button>
              )}
            </div>
          </div>

          {/* Gmail */}
          <div 
            style={{ 
              padding: '16px', 
              background: 'var(--bg-secondary)', 
              borderRadius: '8px'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  background: '#ea4335', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Mail size={20} color="white" />
                </div>
                <div>
                  <div className="font-medium">Gmail / Google Workspace</div>
                  {gmailStatus?.connected ? (
                    <div className="text-sm text-success flex items-center gap-1">
                      <Check size={14} />
                      Connected as {gmailStatus.email}
                      {gmailStatus.connectedAt && (
                        <span className="text-muted"> (since {new Date(gmailStatus.connectedAt).toLocaleDateString()})</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-muted">Not connected</div>
                  )}
                </div>
              </div>
              {gmailStatus?.connected ? (
                <button className="btn btn-outline btn-sm" onClick={handleDisconnectGmail}>
                  Disconnect
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={handleConnectGmail}>
                  <LinkIcon size={14} />
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Admin Only Settings */}
      {isAdmin && (
        <>
          {/* Scheduler Settings */}
          <div className="card mb-6">
            <div 
              className="card-header" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('scheduler')}
            >
              <h2 className="card-title flex items-center gap-2">
                <RefreshCw size={18} />
                Daily Check Scheduler
              </h2>
              {collapsedSections.scheduler ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
            {!collapsedSections.scheduler && (
            <>
            <div className="card-body">
              <p className="text-sm text-muted mb-4">
                Configure when the daily response check job runs. This creates tasks for POs that need follow-up.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select
                    className="form-input"
                    value={config.timezone || 'America/New_York'}
                    onChange={(e) => setConfig(prev => ({ ...prev, timezone: e.target.value }))}
                  >
                    <optgroup label="Americas">
                      <option value="America/New_York">Eastern Time (New York)</option>
                      <option value="America/Chicago">Central Time (Chicago)</option>
                      <option value="America/Denver">Mountain Time (Denver)</option>
                      <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                    </optgroup>
                    <optgroup label="Asia">
                      <option value="Asia/Kolkata">India Standard Time (Kolkata)</option>
                      <option value="Asia/Dubai">Gulf Standard Time (Dubai)</option>
                      <option value="Asia/Singapore">Singapore Time</option>
                      <option value="Asia/Tokyo">Japan Standard Time (Tokyo)</option>
                      <option value="Asia/Shanghai">China Standard Time (Shanghai)</option>
                    </optgroup>
                    <optgroup label="Europe">
                      <option value="Europe/London">GMT/BST (London)</option>
                      <option value="Europe/Paris">Central European Time (Paris)</option>
                      <option value="Europe/Berlin">Central European Time (Berlin)</option>
                    </optgroup>
                    <optgroup label="Others">
                      <option value="UTC">UTC</option>
                      <option value="Australia/Sydney">Australian Eastern Time (Sydney)</option>
                    </optgroup>
                  </select>
                  <span className="text-xs text-muted">
                    Current: {new Date().toLocaleString('en-US', { timeZone: config.timezone || 'America/New_York', timeZoneName: 'short' })}
                  </span>
                </div>

                <div className="form-group">
                  <label className="form-label">Daily Check Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={config.daily_check_time?.substring(0, 5) || '08:00'}
                    onChange={(e) => setConfig(prev => ({ ...prev, daily_check_time: e.target.value + ':00' }))}
                  />
                  <span className="text-xs text-muted">
                    Time in selected timezone when daily check runs
                  </span>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.daily_check_enabled !== false}
                    onChange={(e) => setConfig(prev => ({ ...prev, daily_check_enabled: e.target.checked }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Enable automatic daily check</span>
                </label>
                <span className="text-xs text-muted" style={{ marginLeft: '26px' }}>
                  When enabled, the system will automatically create response check tasks at the scheduled time
                </span>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.allow_admin_task_override !== false}
                    onChange={(e) => setConfig(prev => ({ ...prev, allow_admin_task_override: e.target.checked }))}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Allow admin task override</span>
                </label>
                <span className="text-xs text-muted" style={{ marginLeft: '26px' }}>
                  When enabled, admins can complete tasks assigned to other users
                </span>
              </div>

              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: 'var(--bg-secondary)', 
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}>
                <strong>What happens during daily check:</strong>
                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                  <li>POs in DAY1_SENT for 24+ hours → Creates Day 2 check task</li>
                  <li>POs in DAY2_NO_RESPONSE for 24+ hours → Creates Day 3 check task</li>
                  <li>POs in DAY3_NO_RESPONSE for 24+ hours → Creates Day 4 escalation task</li>
                </ul>
              </div>
            </div>
            <div className="card-footer">
              <button 
                className="btn btn-primary btn-sm" 
                onClick={handleSaveConfig}
                disabled={saving}
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? 'Saving...' : 'Save Scheduler Settings'}
              </button>
            </div>
            </>
            )}
          </div>

          {/* Plugin Configuration - Includes AI, Email, Data Providers */}
          <div className="card mb-6">
            <div 
              className="card-header" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('plugins')}
            >
              <h2 className="card-title flex items-center gap-2">
                <SettingsIcon size={18} />
                Plugin Configuration
              </h2>
              {collapsedSections.plugins ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
            {!collapsedSections.plugins && (
            <div className="card-body">
              <p className="text-sm text-muted mb-4">
                Configure AI provider, email providers, and data integrations for your organization.
              </p>

              {/* AI Configuration Sub-section */}
              <div className="mb-4" style={{ border: '1px solid var(--border)', borderRadius: '8px' }}>
                <div 
                  className="flex items-center justify-between p-3"
                  style={{ 
                    background: 'var(--card-header-bg)', 
                    borderRadius: expandedType === 'AI_CONFIG' ? '8px 8px 0 0' : '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedType(expandedType === 'AI_CONFIG' ? null : 'AI_CONFIG')}
                >
                  <div className="flex items-center gap-2">
                    <Brain size={16} />
                    <strong>AI Configuration</strong>
                    <span className="badge badge-success" style={{ marginLeft: '8px' }}>
                      {config.hasLlmApiKey ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  {expandedType === 'AI_CONFIG' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>

                {expandedType === 'AI_CONFIG' && (
                  <div className="p-3">
                    <p className="text-sm text-muted mb-3">
                      Configure the AI provider for email generation and response parsing.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label className="form-label">AI Provider</label>
                        <select
                          className="form-input"
                          value={config.llmProvider || 'openai'}
                          onChange={(e) => setConfig(prev => ({ ...prev, llmProvider: e.target.value }))}
                        >
                          <option value="openai">OpenAI</option>
                          <option value="anthropic">Anthropic (Claude)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Model</label>
                        <select
                          className="form-input"
                          value={config.llmModel || 'gpt-4'}
                          onChange={(e) => setConfig(prev => ({ ...prev, llmModel: e.target.value }))}
                        >
                          {config.llmProvider === 'anthropic' ? (
                            <>
                              <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                              <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                              <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                            </>
                          ) : (
                            <>
                              <option value="gpt-4">GPT-4</option>
                              <option value="gpt-4-turbo">GPT-4 Turbo</option>
                              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">API Key</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder={config.hasLlmApiKey ? '••••••••••••••••••••••••' : 'Enter API key'}
                        onChange={(e) => setConfig(prev => ({ ...prev, llmApiKey: e.target.value }))}
                      />
                      <span className="text-xs text-muted">
                        {config.hasLlmApiKey ? 'API key is configured. Enter new key to change.' : 'API key not configured.'}
                      </span>
                    </div>

                    <button 
                      className="btn btn-primary btn-sm" 
                      onClick={handleSaveConfig}
                      disabled={saving}
                    >
                      {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                      {saving ? 'Saving...' : 'Save AI Settings'}
                    </button>
                  </div>
                )}
              </div>

              {/* Email and Data Provider Sub-sections */}
              {['EMAIL', 'DATA'].map(type => (
                <div key={type} className="mb-4" style={{ border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <div 
                    className="flex items-center justify-between p-3"
                    style={{ 
                      background: 'var(--card-header-bg)', 
                      borderRadius: expandedType === type ? '8px 8px 0 0' : '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => setExpandedType(expandedType === type ? null : type)}
                  >
                    <div className="flex items-center gap-2">
                      {getPluginIcon(type)}
                      <strong>{getPluginTypeLabel(type)}</strong>
                      <span className="badge badge-secondary" style={{ marginLeft: '8px' }}>
                        {(pluginsByType[type] || []).filter(p => p.isEnabled).length} active
                      </span>
                    </div>
                    {expandedType === type ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>

                  {expandedType === type && (
                    <div className="p-3">
                      {(pluginsByType[type] || []).length === 0 ? (
                        <p className="text-muted text-sm">No providers configured</p>
                      ) : (
                        (pluginsByType[type] || []).map(plugin => (
                          <div 
                            key={plugin.configId} 
                            className="p-3 mb-2"
                            style={{ background: 'var(--bg-secondary)', borderRadius: '6px' }}
                          >
                            {editingPlugin === plugin.configId ? (
                              <div>
                                <div className="form-group">
                                  <label className="form-label">Display Name</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={pluginForm.configName || ''}
                                    onChange={(e) => setPluginForm(prev => ({ ...prev, configName: e.target.value }))}
                                  />
                                </div>
                                {renderPluginConfigFields(plugin)}
                                <div className="form-group">
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={pluginForm.isEnabled || false}
                                      onChange={(e) => setPluginForm(prev => ({ ...prev, isEnabled: e.target.checked }))}
                                    />
                                    <span>Enabled</span>
                                  </label>
                                </div>
                                <div className="flex gap-2">
                                  <button className="btn btn-primary btn-sm" onClick={handleSavePlugin}>
                                    <Save size={14} /> Save
                                  </button>
                                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingPlugin(null)}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{plugin.configName || plugin.providerCode}</div>
                                  <div className="text-sm text-muted">{plugin.providerCode}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {plugin.isEnabled ? (
                                    <span className="badge badge-success">Enabled</span>
                                  ) : (
                                    <span className="badge badge-secondary">Disabled</span>
                                  )}
                                  <button 
                                    className="btn btn-outline btn-sm"
                                    onClick={() => handleEditPlugin(plugin)}
                                  >
                                    <Edit2 size={14} /> Edit
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      
                      {/* Add new provider button */}
                      {availableProviders[type]?.length > 0 && (
                        <button 
                          className="btn btn-outline btn-sm mt-2"
                          onClick={() => handleAddPlugin(type)}
                        >
                          <Plus size={14} /> Add Provider
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Email Templates Sub-section */}
              <div className="mb-4" style={{ border: '1px solid var(--border)', borderRadius: '8px' }}>
                <div 
                  className="flex items-center justify-between p-3"
                  style={{ 
                    background: 'var(--card-header-bg)', 
                    borderRadius: expandedType === 'TEMPLATES' ? '8px 8px 0 0' : '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedType(expandedType === 'TEMPLATES' ? null : 'TEMPLATES')}
                >
                  <div className="flex items-center gap-2">
                    <FileText size={16} />
                    <strong>Email Templates</strong>
                    <span className="badge badge-secondary" style={{ marginLeft: '8px' }}>
                      {emailTemplates.filter(t => t.isActive).length} active
                    </span>
                  </div>
                  {expandedType === 'TEMPLATES' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>

                {expandedType === 'TEMPLATES' && (
                  <div className="p-3">
                    <p className="text-sm text-muted mb-3">
                      Customize email templates for different action types. Use placeholders like {'{{po_number}}'}, {'{{supplier_name}}'}, etc.
                    </p>

                    {emailTemplates.length === 0 ? (
                      <p className="text-muted text-sm">No templates configured.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {emailTemplates.map(template => (
                          <div 
                            key={template.templateId} 
                            style={{ 
                              background: 'var(--bg-secondary)',
                              borderRadius: '8px',
                              overflow: 'hidden'
                            }}
                          >
                            {editingTemplate === template.templateId ? (
                              <div style={{ padding: '16px' }}>
                                <div className="form-group">
                                  <label className="form-label">Template Name</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={templateForm.templateName || ''}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, templateName: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Subject Template</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={templateForm.subjectTemplate || ''}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, subjectTemplate: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Body Template</label>
                                  <textarea
                                    className="form-input"
                                    rows={8}
                                    value={templateForm.bodyTemplate || ''}
                                    onChange={(e) => setTemplateForm(prev => ({ ...prev, bodyTemplate: e.target.value }))}
                                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={templateForm.isActive !== false}
                                      onChange={(e) => setTemplateForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                    />
                                    <span>Active</span>
                                  </label>
                                </div>
                                <div className="flex gap-2">
                                  <button 
                                    className="btn btn-primary btn-sm" 
                                    onClick={handleSaveTemplate}
                                    disabled={savingTemplate}
                                  >
                                    {savingTemplate ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                                    Save
                                  </button>
                                  <button 
                                    className="btn btn-secondary btn-sm" 
                                    onClick={() => setEditingTemplate(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between p-3">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{template.templateName}</span>
                                    <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                                      {template.actionCode}
                                    </span>
                                  </div>
                                  <div className="text-sm text-muted">
                                    Subject: {template.subjectTemplate}
                                  </div>
                                </div>
                                <button 
                                  className="btn btn-outline btn-sm"
                                  onClick={() => handleEditTemplate(template)}
                                >
                                  <Edit2 size={14} /> Edit
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
            )}
          </div>

          {/* Source System Configuration */}
          <div className="card mb-6">
            <div 
              className="card-header" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('sourceSystem')}
            >
              <h2 className="card-title flex items-center gap-2">
                <Database size={18} />
                Source System Integration
              </h2>
              {collapsedSections.sourceSystem ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
            {!collapsedSections.sourceSystem && (
            <div className="card-body">
              {sourceConfigs.length === 0 ? (
                <p className="text-muted">No source systems configured</p>
              ) : (
                sourceConfigs.map(cfg => (
                  <div 
                    key={cfg.purpose} 
                    className="p-3 mb-3" 
                    style={{ background: 'var(--bg-secondary)', borderRadius: '8px' }}
                  >
                    {editingSource === cfg.purpose ? (
                      // Edit Mode
                      <div>
                        <div className="form-group">
                          <label className="form-label">System Name</label>
                          <input
                            type="text"
                            className="form-input"
                            value={sourceForm.systemName}
                            onChange={(e) => setSourceForm(prev => ({ ...prev, systemName: e.target.value }))}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Connection Type</label>
                          <select
                            className="form-input"
                            value={sourceForm.connectionType}
                            onChange={(e) => setSourceForm(prev => ({ ...prev, connectionType: e.target.value }))}
                          >
                            <option value="FILE_UPLOAD">File Upload</option>
                            <option value="API">API</option>
                          </select>
                        </div>

                        {sourceForm.connectionType === 'API' && (
                          <>
                            <div className="form-group">
                              <label className="form-label">API Endpoint</label>
                              <input
                                type="url"
                                className="form-input"
                                value={sourceForm.apiEndpoint}
                                onChange={(e) => setSourceForm(prev => ({ ...prev, apiEndpoint: e.target.value }))}
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">API Key</label>
                              <input
                                type="password"
                                className="form-input"
                                placeholder="Enter new API key or leave blank"
                                value={sourceForm.apiKey}
                                onChange={(e) => setSourceForm(prev => ({ ...prev, apiKey: e.target.value }))}
                              />
                            </div>
                          </>
                        )}

                        {sourceForm.connectionType === 'FILE_UPLOAD' && (
                          <div className="form-group">
                            <label className="form-label">File Format</label>
                            <select
                              className="form-input"
                              value={sourceForm.fileFormat}
                              onChange={(e) => setSourceForm(prev => ({ ...prev, fileFormat: e.target.value }))}
                            >
                              <option value="EXCEL">Excel (.xlsx)</option>
                              <option value="CSV">CSV</option>
                            </select>
                          </div>
                        )}

                        <div className="form-group">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={sourceForm.isEnabled}
                              onChange={(e) => setSourceForm(prev => ({ ...prev, isEnabled: e.target.checked }))}
                            />
                            <span>Enabled</span>
                          </label>
                        </div>

                        <div className="flex gap-2">
                          <button className="btn btn-primary btn-sm" onClick={handleSaveSource}>
                            Save
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditingSource(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {cfg.purpose === 'DATA_IMPORT' ? (
                                <Upload size={16} className="text-muted" />
                              ) : (
                                <Download size={16} className="text-muted" />
                              )}
                              <strong>{cfg.purpose === 'DATA_IMPORT' ? 'Data Import' : 'Status Update'}</strong>
                              <span className={`badge badge-${cfg.isEnabled ? 'success' : 'secondary'}`}>
                                {cfg.isEnabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <div className="text-sm text-muted">
                              System: {cfg.systemName} | 
                              Connection: {cfg.connectionType === 'API' ? 'API' : `File Upload (${cfg.fileFormat})`}
                            </div>
                            {cfg.lastSyncAt && (
                              <div className="text-sm text-muted mt-1">
                                Last sync: {new Date(cfg.lastSyncAt).toLocaleString()} - {cfg.lastSyncStatus}
                              </div>
                            )}
                          </div>
                          <button 
                            className="btn btn-outline btn-sm"
                            onClick={() => handleEditSource(cfg)}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            )}
          </div>

          {/* Branding */}
          <div className="card mb-6">
            <div 
              className="card-header" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('branding')}
            >
              <h2 className="card-title flex items-center gap-2">
                <SettingsIcon size={18} />
                Branding
              </h2>
              {collapsedSections.branding ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
            {!collapsedSections.branding && (
            <div className="card-body">
              <p className="text-sm text-muted mb-4">
                Customize the application appearance. Changes apply to all users in this organization.
              </p>
              <div className="form-group">
                <label className="form-label">Primary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.primaryColor || '#f75900'}
                    onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                    style={{ width: '48px', height: '38px', padding: '2px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={config.primaryColor || '#f75900'}
                    onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                  />
                </div>
                <span className="text-xs text-muted">
                  This color will be applied to buttons, links, and accents throughout the application.
                </span>
              </div>

              <div className="form-group">
                <label className="form-label">Logo URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={config.logoUrl || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, logoUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              
              <button 
                className="btn btn-primary btn-sm mt-3" 
                onClick={handleSaveConfig}
                disabled={saving}
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? 'Saving...' : 'Save Branding'}
              </button>
            </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Settings;